import { node, icon } from "~/util/dom";
import { _ } from "~/i18n";
import createSDSIcon from "~/ui/icon";
import { MYMAPS_DELETE, MYMAPS_DUPLICATE, MYMAPS_EDIT, MYMAPS_FOLDER_LINK, MYMAPS_FOLDER_MOVE, MYMAPS_FOLDER_REMOVE, MYMAPS_MULTIPLE, MYMAPS_SHARE, MYMAPS_STAR_EMPTY } from "~/ui/sds-compatible-icons";
import { IMapyBoxItem } from "~/interfaces";
import { trackListFolders } from "~/util/providers/mapybox";
import { deferPromise } from "~/util/general";

/**
 * Contextmenu pro novy batuzek.
 * Mozne jmena: share (sdileni), duplicate (duplikace), move-to-folder (pridat do slozky), navigate (navigace, routovac), rename (prejmenovat, editovat), multiple (hromadne operace), delete (mazani)
 * signaly:
 * clickCb(type)
 */

export type TMymapsContextMenuName = "add-to-folder" | "delete" | "remove" | "delete-from-all" | "edit" | "export" | "share" | "export" | "link-to-folder" | "link-to-another-folder" | "report" | "save-as-new" | "move-to-folder"
	| "activity-move-to-folder" | "activity-remove" | "navigate" | "rename" | "download" | "duplicate" | "import" | "multiple";

export type TMymapsContextMenuPlaces = "none" | "places-list" | "place-detail" | "activities-list" | "activity-detail" | "box-normal" | "box-activity" | "path-detail" | "usermarks-detail" | "distancemeter-detail";

interface IOpts {
	// callback pri zavreni
	clickCb?: (name: TMymapsContextMenuName) => void;
	// callback pri zavreni
	onClose?: () => void;
	// pripojit do DOMu?
	parent?: HTMLElement;
	// třída tlačítka (abych věděl, že klikám na jedno z tlačítek)
	closestSelector?: string;
	// jednotlive jmena tlacitek
	names?: Array<TMymapsContextMenuName>;
	// data pro build podle dat batuzku
	mapyboxItem?: IMapyBoxItem;
	// misto?
	place?: TMymapsContextMenuPlaces;
}

const contextMenuHandler: { [key: string]: MyMapsContextMenu; } = {};

export function getIcon(name: TMymapsContextMenuName): HTMLElement | SVGElement {
	switch (name) {
		case "share":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-share", symbol: MYMAPS_SHARE });

		case "duplicate":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-duplicate", symbol: MYMAPS_DUPLICATE });

		case "edit":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-edit", symbol: MYMAPS_EDIT });

		case "save-as-new":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-star-empty", symbol: MYMAPS_STAR_EMPTY });

		case "add-to-folder":
			return icon("move-to-folder");

		case "link-to-another-folder":
		case "link-to-folder":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-folder-link", symbol: MYMAPS_FOLDER_LINK });

		case "move-to-folder":
		case "activity-move-to-folder":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-folder-move", symbol: MYMAPS_FOLDER_MOVE });

		case "delete":
		case "delete-from-all":
		case "remove":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-folder-delete", symbol: MYMAPS_DELETE });

		case "activity-remove":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-folder-remove", symbol: MYMAPS_FOLDER_REMOVE });

		case "multiple":
			return createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-multiple", symbol: MYMAPS_MULTIPLE });

		case "import":
			return icon("import2");

		case "export":
		case "download":
			return icon("export2");

		default:
	}

	return icon(name);
}

export function getTitle(name: TMymapsContextMenuName): string {
	switch (name) {
		case "save-as-new":
			return _("save");

		case "edit":
			return _("mymap.contextMenu.editProps");

		case "delete-from-all":
			return _("mymap.contextMenu.deleteFromAll");

		case "delete":
		case "remove":
			return _("mymap.contextMenu.delete");

		case "activity-remove":
			return _("mymap.activity.removeFromFolder");

		case "activity-move-to-folder":
			return _("mymap.activity.moveToOtherFolder");

		case "multiple":
			return _("mymap.contextMenu.multiple2");

		default:
	}

	return _(`mymap.contextMenu.${name}`);
}

/**
	 * Vytvoreni itemu a naplneni do pole.
	 */
function createItem(name: TMymapsContextMenuName): HTMLLIElement {
	const el = node("li", { className: `contextmenu-item contextmenu-item-name-${name}` });
	const iconElm = node("span", { className: "icon" });

	iconElm.appendChild(getIcon(name));

	// hack kvuli tomu, jak natvrdo je menu zadratovane
	const title = node("h3", { className: "name", textContent: getTitle(name) });

	el.appendChild(iconElm);
	el.appendChild(title);

	return el;
}

export default class MyMapsContextMenu {
	_dom: {
		container: HTMLUListElement;
		[key: string]: HTMLElement;
	};

	_opened: boolean;

	_items: Array<{
		el: HTMLLIElement;
		name: TMymapsContextMenuName;
	}>;

	_opts: IOpts;

	_deferPromData: ReturnType<typeof deferPromise>;

	_id: string;

	constructor(opts: IOpts) {
		this._opts = {
			clickCb: null,
			onClose: null,
			parent: null,
			closestSelector: "",
			names: [],
			mapyboxItem: null,
			place: "none",
			...opts,
		};
		this._dom = {
			container: node("ul", { className: "mymap-contextmenu" }) as HTMLUListElement,
		};
		this._items = [];
		this._opened = false;
		this._deferPromData = deferPromise();
		this._id = Math.random().toString(16).replace("0.", "");

		// ulozime do kese
		contextMenuHandler[this._id] = this;

		this._build();
	}

	/**
	 * Ziskani obalu.
	 */
	get container() {
		return this._dom.container;
	}

	/**
	 * Nastaveni rodice, kam se ma contextmenu pripnout.
	 *
	 * @param {Element} parent Rodic
	 */
	set parent(parent: HTMLElement) {
		this._opts.parent = parent;
	}

	/**
	 * Nastaví selektor tlačítek
	 * @type {String} selektor tlačítka
	 */
	set closestSelector(selector: string) {
		this._opts.closestSelector = selector;
	}

	/**
	 * Je menu otevrene?
	 */
	isOpen() {
		return this._opened;
	}

	/**
	 * Otevreni - naplneni dat.
	 */
	async open() {
		if (this._opened) {
			return;
		}

		// zjistime, jestli jsou otevrene nejake jine
		const allIds = Object.keys(contextMenuHandler);

		for (const id of allIds) {
			if (id !== this._id) {
				const otherContextMenu = contextMenuHandler[id];

				// zavreme jej
				if (otherContextMenu.isOpen()) {
					otherContextMenu.close();
				}
			}
		}

		await this._deferPromData.promise;

		// jinak se hnedka provedl handleEvent
		setTimeout(() => {
			document.addEventListener("click", this, false);
		}, 0);

		document.addEventListener("keydown", this);

		this._items.forEach(item => {
			this._dom.container.appendChild(item.el);
		});

		if (this._opts.parent) {
			this._opts.parent.appendChild(this._dom.container);
		}

		this._opened = true;
	}

	/**
	 * Zavreni - odepnuti dat.
	 */
	close() {
		if (!this._opened) {
			return;
		}

		document.removeEventListener("click", this, false);
		document.removeEventListener("keydown", this);

		this._items.forEach(item => {
			item.el.remove();
		});

		if (this._opts.parent) {
			this._dom.container.remove();
		}

		this._opened = false;
		this._opts.onClose && this._opts.onClose();
	}

	/**
	 * Prepinani zavreni/otevreni.
	 */
	toggle() {
		if (this._opened) {
			this.close();
		} else {
			this.open();
		}
	}

	handleEvent(event: Event) {
		if (!this._opened) {
			return;
		}

		switch (event.type) {
			case "click": {
				//klikám na svoje tlačítko nebo jiné tlačítko
				const target = (event as MouseEvent).target as HTMLLIElement;

				if ((this._opts.parent && this._opts.parent.contains(target)) || !(this._opts.closestSelector && target.closest(this._opts.closestSelector))) {
					event.stopPropagation();
				}

				event.preventDefault();

				if (this._dom.container.contains(target)) {
					const len = this._items.length;

					for (let ind = 0; ind < len; ind++) {
						const item = this._items[ind];

						if (item.el.contains(target)) {
							if (this._opts.clickCb && typeof this._opts.clickCb === "function") {
								this._opts.clickCb(item.name);
								this.close();
							}
							break;
						}
					}
				} else {
					this.close();
				}
				break;
			}

			case "keydown": {
				event.stopPropagation();
				event.preventDefault();

				const keyCode = (event as KeyboardEvent).which || (event as KeyboardEvent).keyCode;

				// esc
				if (keyCode === 27) {
					this.close();
				}
				break;
			}

			default:
		}
	}

	async _build() {
		let names: Array<TMymapsContextMenuName> = this._opts.names;

		if (!names.length) {
			if (this._opts.mapyboxItem?.foreign) {
				names = ["add-to-folder"];
			} else {
				switch (this._opts.place) {
					case "activities-list": {
						const links = await trackListFolders(this._opts.mapyboxItem.id);
						const hasLinks = links.length > 0;

						names = [
							"share",
							"edit",
							"export",
							...hasLinks
								? [
									"link-to-another-folder",
									"delete-from-all",
								] as Array<TMymapsContextMenuName>
								: [
									"link-to-folder",
									"delete",
								] as Array<TMymapsContextMenuName>,
						];
						break;
					}

					case "activity-detail": {
						const links = await trackListFolders(this._opts.mapyboxItem.id);
						const hasLinks = links.length > 0;
						const isTrackLinkActivity = this._opts.mapyboxItem?.linktype === "track";

						names = [
							"edit",
							"export",
							hasLinks || isTrackLinkActivity
								? "link-to-another-folder"
								: "link-to-folder",
							...links.length === 1
								? [
									"activity-move-to-folder",
									"activity-remove",
								] as Array<TMymapsContextMenuName>
								: [
								],
							hasLinks || isTrackLinkActivity
								? "delete-from-all"
								: "delete",
						];
						break;
					}

					case "places-list":
					case "place-detail": {
						const isTrackLink = this._opts.mapyboxItem.type === "link" && this._opts.mapyboxItem.linktype === "track";
						const isFolder = this._opts.mapyboxItem.type === "folder";

						if (!(isFolder && this._opts.place === "place-detail")) {
							names.push("share");
						}

						if (this._opts.mapyboxItem.type === "entity" || this._opts.mapyboxItem.type === "point") {
							names.push("navigate");
						}

						if (isFolder) {
							names.push("duplicate");
						}

						names.push("edit");

						// exportovat
						if (
							["folder", "route", "set", "measure", "path"].includes(this._opts.mapyboxItem.type)
							|| isTrackLink
						) {
							names.push("download");
						}

						if (isTrackLink) {
							names.push("link-to-another-folder");
							names.push("activity-move-to-folder");
						}

						if (this._opts.mapyboxItem.type !== "folder" && !isTrackLink) {
							names.push("save-as-new");
							names.push("move-to-folder");
						}

						if (isFolder) {
							names.push("import");
						}

						// hromadne upravy
						if (isFolder && this._opts.place === "place-detail") {
							names.push("multiple");
						}

						if (isTrackLink) {
							names.push("activity-remove");
							// nepotrebujeme be - logicky, pokud jsem v batuzku a je zde linky, tak je pocet linku min. 1
							names.push("delete-from-all");
						} else {
							names.push("delete");
						}

						break;
					}

					case "path-detail":
						names = [
							"edit",
							"export",
							"save-as-new",
							"move-to-folder",
							"delete",
						];
						break;

					case "box-normal":
						names = [
							"move-to-folder",
							"remove",
						];
						break;

					case "box-activity":
						names = [
							"activity-move-to-folder",
							"activity-remove",
						];
						break;

					case "usermarks-detail":
					case "distancemeter-detail":
						names = [
							"edit",
							"move-to-folder",
							"delete",
						];
						break;

					default:
				}
			}
		}

		this._deferPromData.resolve({});
		this._create(names);
	}

	/**
	 * Vytvorime polozky
	 *
	 * @param {Array} names Pole s nazvama tlacitek
	 */
	_create(names?: Array<TMymapsContextMenuName>) {
		const source = names && Array.isArray(names) ? names : this._opts.names;

		source.forEach(name => {
			this._items.push({
				el: createItem(name),
				name,
			});
		});
	}
}
